import { ChakraProvider, Box, Flex, Spinner, useToast } from '@chakra-ui/react';
import { PageSkeleton } from './components/skeletons';
import theme from './theme';
import { DEFAULT_TOAST_OPTIONS } from './ui/primitives/Toast';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { SessionProvider } from './contexts/SessionContext';
import { BreadcrumbProvider, useBreadcrumbs } from './contexts/BreadcrumbContext';
import { BulkActionsShortcutProvider } from './contexts/BulkActionsShortcutContext';
import {
	SidebarCollapseOverrideProvider,
	useSidebarForceCollapsed,
} from './contexts/SidebarCollapseOverrideContext';
import { useContext, useMemo, useState, Component, ReactNode, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import posthog from 'posthog-js';
import SessionContext, { isInventoryOrgSession } from './contexts/SessionContext';
import { getInvoicingReportingVisibility } from './utils/invoicingToolAccess';
import { isEmbedMode } from './utils/embedMode';
import { canUseAskCarl } from './utils/askCarlAccess';
import { canOpenReporting } from './utils/inventoryReportingAccess';
import Login from './pages/Login';
import CreateAccount from './pages/CreateAccount';
import InviteAccount from './pages/InviteAccount';
import AccountCreated from './pages/AccountCreated';
import Settings from './pages/Settings';
import SubscriptionSuccess from './pages/SubscriptionSuccess';
import ForgotPassword from './pages/ForgotPassword';
import Status from './pages/Status';
import TextUpdatesOptIn from './pages/text-updates/TextUpdatesOptIn';
import AppNavRail from './components/AppNavRail';
import TopBar from './components/TopBar';
import { useOverlayNav } from './hooks/useOverlayNav';
import Error404 from './pages/error/Error404';
import Error500 from './pages/error/Error500';
import { ExportsPage } from './features/exports';
import Home from './pages/Home';
import { OrdersPage, OrderDetailPage } from './features/orders';
import DeliveryBoard from './pages/DeliveryBoard';
import AskCarl from './pages/AskCarl';
import Chats from './pages/Chats';
import InvoicesFeed from './pages/invoice/InvoicesFeed';
import InvoiceDetail from './pages/invoice/InvoiceDetail';
import ReportingPage from './pages/reporting/ReportingPage';
import InventoryPage from './pages/inventory/InventoryPage';
import SupplierReconcileReview from './pages/SupplierReconcileReview';
import CccVendorReconcile from './pages/CccVendorReconcile';
import RecentActivityTracker from './components/RecentActivityTracker';
import { NotificationToastManager, AnnouncementBanner } from './components/notifications';
import InvoiceUpdateJobWatcher from './components/invoice/InvoiceUpdateJobWatcher';
import ExtractionObservationJobWatcher from './components/invoice/ExtractionObservationJobWatcher';
import { useGetNotifications } from './hooks/useGetNotifications';
import { useMarkAnnouncementsRead } from './hooks/useMarkAnnouncementsRead';
import EMSDetailModal from './modals/EMSDetailModal';
import KeyboardShortcutsModal from './modals/KeyboardShortcutsModal';
import { API_HOST, API_KEY } from './constants/posthog';
import { BRAND_NAME } from './constants/brand';

declare global {
	interface Window {
		pylon?: {
			chat_settings: {
				app_id: string;
				email: string;
				name: string;
				avatar_url?: string;
				email_hash?: string;
			};
		};
		Pylon?: (method: string, callback?: (count: number) => void) => void;
	}
}

class ErrorBoundary extends Component<{ children: ReactNode; }, { hasError: boolean; }> {
	constructor(props: { children: ReactNode; }) {
		super(props);
		this.state = { hasError: false };
	}

	static getDerivedStateFromError(): { hasError: boolean; } {
		return { hasError: true };
	}

	componentDidCatch(error: Error, errorInfo: any) {
		console.error('Application crashed:', error, errorInfo);
	}

	render() {
		if (this.state.hasError) {
			return <Error500 />;
		}

		return this.props.children;
	}
}

interface ProtectedRouteProps {
	children: React.ReactNode;
}

/**
 * Personal settings live under /settings but are open to every signed-in user,
 * unlike the rest of /settings which stays admin-only for the minimal roles.
 */
function isPersonalSettingsPath(pathname: string): boolean {
	return pathname.startsWith('/settings/profile') || pathname.startsWith('/settings/notifications');
}

/** Yaxle team managers manage employees from whichever org they're in, whatever its shell. */
function isYaxleTeamPath(pathname: string, canManageTeam: boolean): boolean {
	return canManageTeam && pathname.startsWith('/settings/yaxle-team');
}

/**
 * Paths invoicing role or invoicing-mode org may load under {@link ProtectedRoute}.
 * Anything else redirects to `/invoices`. Add a prefix here when introducing a new
 * invoicing-safe page (most app routes stay full-org only).
 */
function isInvoicingMinimalAllowedPath(pathname: string, isAdmin: boolean, canManageTeam: boolean): boolean {
	if (pathname.startsWith('/invoices')) return true;
	if (isPersonalSettingsPath(pathname)) return true;
	if (isYaxleTeamPath(pathname, canManageTeam)) return true;
	if (pathname.startsWith('/account-created')) return true;
	if (pathname.startsWith('/subscription/success')) return true;
	if (isAdmin && pathname.startsWith('/settings')) return true;
	return false;
}

/**
 * Paths technician users may load under {@link ProtectedRoute}.
 * Anything else redirects to `/inventory`.
 */
function isTechnicianMinimalAllowedPath(pathname: string, isAdmin: boolean, canManageTeam: boolean): boolean {
	if (pathname.startsWith('/inventory')) return true;
	if (isPersonalSettingsPath(pathname)) return true;
	if (isYaxleTeamPath(pathname, canManageTeam)) return true;
	if (pathname.startsWith('/account-created')) return true;
	if (pathname.startsWith('/subscription/success')) return true;
	if (isAdmin && pathname.startsWith('/settings')) return true;
	return false;
}

const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
	const sessionContext = useContext(SessionContext);
	const location = useLocation();
	const navigate = useNavigate();
	const toast = useToast();
	const previousUnreadCount = useRef<number>(0);
	const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
		const saved = localStorage.getItem('sidebar_collapsed');
		return saved === 'true';
	});
	// Pages (e.g. OrderDetail drawers) can temporarily force-collapse the rail without
	// writing localStorage — the user's saved preference is restored when the override clears.
	const forceSidebarCollapsed = useSidebarForceCollapsed();
	const effectiveIsSidebarCollapsed = isSidebarCollapsed || forceSidebarCollapsed;
	// Drawer open/close is instant (`INSTANT_SIDE_DRAWER_MOTION_PROPS`). Suppress the
	// rail/main width transitions for that flip so the LHS stays in lockstep — same
	// pattern as OrderDetail's RHS customer sidebar. Manual Cmd+. / chevron keeps 100ms.
	const prevForceSidebarCollapsedRef = useRef(forceSidebarCollapsed);
	const forceSidebarCollapsedJustChanged =
		prevForceSidebarCollapsedRef.current !== forceSidebarCollapsed;
	const [suppressSidebarTransition, setSuppressSidebarTransition] = useState(false);
	useLayoutEffect(() => {
		if (!forceSidebarCollapsedJustChanged) return;
		prevForceSidebarCollapsedRef.current = forceSidebarCollapsed;
		setSuppressSidebarTransition(true);
		const raf = requestAnimationFrame(() => setSuppressSidebarTransition(false));
		return () => cancelAnimationFrame(raf);
	}, [forceSidebarCollapsed, forceSidebarCollapsedJustChanged]);
	const skipSidebarTransition =
		forceSidebarCollapsed ||
		suppressSidebarTransition ||
		forceSidebarCollapsedJustChanged;
	const sidebarWidthTransition = skipSidebarTransition ? 'none' : 'width 100ms ease-out';
	const [isKeyboardShortcutsOpen, setIsKeyboardShortcutsOpen] = useState(false);
	const [selectedEmsId, setSelectedEmsId] = useState<string | null>(null);
	const [isEmsModalOpen, setIsEmsModalOpen] = useState(false);
	const [isNavOpen, setIsNavOpen] = useState(false);
	const isOverlayNav = useOverlayNav();
	/** Same allowed routes as invoicing-only sidebar: invoicing role or invoicing org. */
	const isInvoicingMinimalApp = sessionContext?.tabs && sessionContext.tabs.includes('invoicing') && sessionContext.tabs.length === 1;
	/** Technician role: inventory-only shell (mirrors invoicing role → invoices-only). */
	const isTechnicianMinimalApp = sessionContext?.tabs && sessionContext.tabs.includes('inventory') && sessionContext.tabs.length === 1;
	/** Inventory-only org: technician shell plus Reporting (Inventory metrics) for executives/GMs. */
	const isInventoryOrgApp = isInventoryOrgSession(sessionContext);
	const invoicingReportingVisibility = getInvoicingReportingVisibility(sessionContext);
	const canManageInternalTeam = sessionContext?.isAuthenticated === true && sessionContext.canManageInternalTeam;
	const canAccessCurrentPath = useMemo(() => {
		if (isInventoryOrgApp) {
			if (isTechnicianMinimalAllowedPath(location.pathname, sessionContext?.isAdmin === true, canManageInternalTeam)) return true;
			return canOpenReporting(sessionContext) && location.pathname.startsWith('/reporting');
		}
		if (isTechnicianMinimalApp) {
			return isTechnicianMinimalAllowedPath(location.pathname, sessionContext?.isAdmin === true, canManageInternalTeam);
		}
		if (!isInvoicingMinimalApp) return true;
		if (isInvoicingMinimalAllowedPath(location.pathname, sessionContext?.isAdmin === true, canManageInternalTeam)) return true;
		if (invoicingReportingVisibility === 'invoicing-only') {
			if (location.pathname.startsWith('/reporting')) return true;
		}
		return false;
	}, [isInventoryOrgApp, isTechnicianMinimalApp, isInvoicingMinimalApp, invoicingReportingVisibility, location.pathname, sessionContext, canManageInternalTeam]);
	/**
	 * Embed mode (`?embed=1`): render the page full-bleed with no sidebar,
	 * topbar, or banners. Used when a page is loaded inside a host frame such
	 * as the partbay-mobile Ask Carl WebView, which injects the session and
	 * wants only the page itself. Sticky for the session (see embedMode.ts)
	 * so SPA navigations that drop the query param can't fall back into the
	 * full desktop shell.
	 */
	const isEmbedded = useMemo(
		() => isEmbedMode(location.search),
		[location.search],
	);
	const settingsShortcutPath = useMemo(() => (
		sessionContext?.permissions && sessionContext.permissions.some(p => p === 'settings' || p === 'preferences' || p === 'it-settings') ? '/settings' : '/settings/profile'
	), [sessionContext?.permissions]);
	const shortcutModifierLabel = useMemo<'Cmd' | 'Ctrl'>(() => {
		if (typeof navigator === 'undefined') return 'Ctrl';
		return /Mac|iPhone|iPad|iPod/i.test(navigator.userAgent) ? 'Cmd' : 'Ctrl';
	}, []);

	// Announcements piggyback on the notifications poll (no extra polling). The
	// most recent unread one renders in flow between the top bar and the
	// content row, so it pushes content down without any height measuring.
	const { data: notificationsData } = useGetNotifications();
	const markAnnouncementsRead = useMarkAnnouncementsRead();
	const unreadAnnouncements = notificationsData?.announcements ?? [];
	const activeAnnouncement = unreadAnnouncements[0];

	const dismissAnnouncement = useCallback(() => {
		if (!activeAnnouncement) return;
		markAnnouncementsRead.mutate({ announcementIds: [activeAnnouncement._id] });
	}, [activeAnnouncement, markAnnouncementsRead]);

	const handleOpenEmsModal = (emsId: string) => {
		setSelectedEmsId(emsId);
		setIsEmsModalOpen(true);
	};

	const handleCloseEmsModal = () => {
		setIsEmsModalOpen(false);
		setSelectedEmsId(null);
	};

	// Settings renders as a full-screen takeover with its own Back button.
	// Remember the last page outside /settings so Back knows where to return.
	const isSettingsTakeover = location.pathname.startsWith('/settings');
	useEffect(() => {
		if (!isSettingsTakeover) {
			sessionStorage.setItem('settingsReturnPath', location.pathname + location.search);
		}
	}, [isSettingsTakeover, location.pathname, location.search]);

	/*
	 * Browser tab + history titles:
	 *   list     → "Orders | Kniesel's Collision"
	 *   drill-in → "165142 | Kniesel's Collision"
	 * The sidebar tab drops off once a row is open so history stays two
	 * segments. Pages feed the left side through the breadcrumb context
	 * (usePageTitle / setBreadcrumbs); chrome-less surfaces fall back to a
	 * route-prefix label.
	 */
	const { items: crumbItems, currentPage: crumbPage, documentTitle: crumbTitle } = useBreadcrumbs();
	useEffect(() => {
		const FALLBACK_LABELS: Array<[prefix: string, label: string]> = [
			['/settings', 'Settings'],
			['/chat', 'Chats'],
			['/status', 'Status'],
		];
		const org = sessionContext?.organizationName ?? null;
		const tab =
			crumbPage
			?? FALLBACK_LABELS.find(([prefix]) => location.pathname.startsWith(prefix))?.[1]
			?? null;
		// A crumb trail means we drilled into a row — prefer the entity leaf
		// (RO / invoice number) and drop the sidebar tab.
		const isDrillDown = crumbItems.length > 0;
		const entity = crumbTitle ?? (isDrillDown ? crumbPage : null);
		const left = entity ?? tab;
		const parts = [left, org].filter(Boolean);
		document.title = parts.length ? parts.join(' | ') : BRAND_NAME;
	}, [crumbTitle, crumbPage, crumbItems, sessionContext?.organizationName, location.pathname]);

	useEffect(() => {
		if (sessionContext?.isAuthenticated && isInvoicingMinimalApp) {
			posthog.capture('invoicing_shell_enabled', {
				role: sessionContext.position,
				organizationMode: sessionContext.organizationMode,
				organizationId: sessionContext.organizationId,
				locationId: sessionContext.locationId,
			});
		}
	}, [sessionContext, isInvoicingMinimalApp]);

	useEffect(() => {
		if (!sessionContext?.isAuthenticated) return;
		// Embed mode (mobile WebView) hides support entirely — chat_settings is
		// never set, so the widget does not boot at all.
		if (isEmbedded) {
			window.Pylon?.('hideChatBubble');
			return;
		}
		window.pylon = {
			chat_settings: {
				app_id: "69d3b6cb-1308-45da-ae1e-ec6038468d57",
				email: sessionContext.email,
				name: `${sessionContext.firstName} ${sessionContext.lastName}`,
				email_hash: sessionContext.pylonEmailHash,
			},
		};
		window.Pylon?.('hideChatBubble');
		window.Pylon?.('onHide', () => window.Pylon?.('hideChatBubble'));
	}, [sessionContext?.isAuthenticated, sessionContext?.email, sessionContext?.firstName, sessionContext?.lastName, sessionContext?.pylonEmailHash, isEmbedded]);

	// Listen for new Pylon chat messages and show toast notification
	useEffect(() => {
		if (sessionContext?.isAuthenticated && window.Pylon) {
			window.Pylon("onChangeUnreadMessagesCount", (unreadCount: number) => {
				// Only show toast when count increases (new message received)
				if (unreadCount > previousUnreadCount.current && previousUnreadCount.current >= 0) {
					toast({
						title: "New message from support",
						description: unreadCount === 1
							? "You have 1 unread message"
							: `You have ${unreadCount} unread messages`,
						status: "info",
						duration: 5000,
						isClosable: true,
					});
				}
				previousUnreadCount.current = unreadCount;
			});
		}
	}, [sessionContext?.isAuthenticated, toast]);

	const toggleSidebarCollapse = useCallback(() => {
		setIsSidebarCollapsed((previous) => {
			const next = !previous;
			localStorage.setItem('sidebar_collapsed', String(next));
			return next;
		});
	}, []);

	const toggleNav = useCallback(() => {
		if (isOverlayNav) {
			setIsNavOpen((open) => !open);
			return;
		}
		toggleSidebarCollapse();
	}, [isOverlayNav, toggleSidebarCollapse]);

	useEffect(() => {
		setIsNavOpen(false);
	}, [location.pathname]);

	useEffect(() => {
		if (!isOverlayNav) setIsNavOpen(false);
	}, [isOverlayNav]);

	useEffect(() => {
		if (!isNavOpen) return undefined;
		const onKey = (event: KeyboardEvent) => {
			if (event.key === 'Escape') setIsNavOpen(false);
		};
		document.addEventListener('keydown', onKey);
		return () => document.removeEventListener('keydown', onKey);
	}, [isNavOpen]);

	/**
	 * App shell — Brex-style row (ENG-2297): the nav rail owns the full left
	 * edge top-to-bottom; TopBar + main stack in a column beside it, so the
	 * TopBar's bottom border stops at the rail instead of crossing it.
	 * Everything sizes itself (flex/%), no fixed offsets or measured heights.
	 */
	const renderAppShell = (content: ReactNode, overlays?: ReactNode) => (
		<Flex direction="row" h="100dvh" overflow="hidden" bg="gray.50">
			<AppNavRail
				isOverlayNav={isOverlayNav}
				isNavOpen={isNavOpen}
				onCloseNav={() => setIsNavOpen(false)}
				sidebarWidthTransition={sidebarWidthTransition}
				effectiveIsSidebarCollapsed={effectiveIsSidebarCollapsed}
				toggleSidebarCollapse={toggleSidebarCollapse}
			/>
			<Flex direction="column" flex="1" minW={0}>
				<TopBar
					isSidebarCollapsed={effectiveIsSidebarCollapsed}
					onToggleSidebar={toggleNav}
					isOverlayNav={isOverlayNav}
					isNavOpen={isNavOpen}
				/>
				{activeAnnouncement && (
					<AnnouncementBanner
						announcement={activeAnnouncement}
						extraCount={Math.max(unreadAnnouncements.length - 1, 0)}
						onDismiss={dismissAnnouncement}
						isDismissing={markAnnouncementsRead.isPending}
					/>
				)}
				<Box
					as="main"
					flex="1"
					minW={0}
					minH={0}
					p={4}
					overflowY="auto"
					display="flex"
					flexDirection="column"
					alignItems="center"
					bg="white"
				>
					{content}
				</Box>
			</Flex>
			{overlays}
		</Flex>
	);

	useEffect(() => {
		if (!sessionContext?.isAuthenticated) {
			return;
		}

		const handleGlobalShortcuts = (event: KeyboardEvent) => {
			if (!(event.metaKey || event.ctrlKey) || event.repeat) return;

			if (event.code === 'Slash') {
				event.preventDefault();
				setIsKeyboardShortcutsOpen((previous) => !previous);
				return;
			}

			if (event.code === 'Period' && !event.shiftKey) {
				event.preventDefault();
				toggleNav();
				return;
			}

			if (event.code === 'Comma' && event.shiftKey) {
				event.preventDefault();
				setIsKeyboardShortcutsOpen(false);
				navigate(settingsShortcutPath);
			}
		};

		document.addEventListener('keydown', handleGlobalShortcuts);
		return () => document.removeEventListener('keydown', handleGlobalShortcuts);
	}, [navigate, sessionContext?.isAuthenticated, settingsShortcutPath, toggleNav]);

	if (sessionContext?.loading) {
		if (isEmbedded) {
			return (
				<Box position="fixed" inset={0} bg="white" display="flex" flexDirection="column" alignItems="center" overflowY="auto" p={4}>
					<PageSkeleton />
				</Box>
			);
		}
		return renderAppShell(<PageSkeleton />);
	}
	if (!sessionContext?.isAuthenticated) {
		return <Navigate to="/login" replace />;
	}
	// Embed mode has no shell, so the minimal-shell path restrictions don't
	// apply — and their redirects (/invoices, /inventory) would ping-pong
	// against the embed route lock below.
	if (!canAccessCurrentPath && !isEmbedded) {
		if (isInventoryOrgApp) {
			posthog.capture('inventory_org_route_redirected', {
				path: location.pathname,
				isAdmin: sessionContext?.isAdmin,
			});
			return <Navigate to="/inventory" replace />;
		}
		if (isTechnicianMinimalApp) {
			posthog.capture('technician_route_redirected', {
				path: location.pathname,
				isAdmin: sessionContext?.isAdmin,
			});
			return <Navigate to="/inventory" replace />;
		}
		posthog.capture('invoicing_route_redirected', {
			path: location.pathname,
			isAdmin: sessionContext?.isAdmin,
		});
		return <Navigate to="/invoices" replace />;
	}

	// Embed mode: page only, no app chrome (mobile WebView host, etc.).
	// gray.50 matches the Ask Carl surface so overscroll/safe-area gaps never
	// flash white. overflow hidden (not auto) keeps the composer pinned —
	// Ask Carl scrolls its own message list; a scrolling <main> fights the
	// visualViewport keyboard inset.
	if (isEmbedded) {
		// The embed hosts exactly one page. Any navigation elsewhere (a missed
		// link, a card's "open order" action, a deep link) would render main
		// site content inside the native shell, so bounce straight back.
		if (!location.pathname.startsWith('/ask-carl')) {
			return <Navigate to="/ask-carl?embed=1" replace />;
		}
		return (
			<Box position="relative" minH="100dvh" bg="gray.50">
				<RecentActivityTracker />
				<Box
					as="main"
					position="fixed"
					inset={0}
					overflow="hidden"
					display="flex"
					flexDirection="column"
					bg="gray.50"
					zIndex={20}
				>
					{children}
				</Box>
			</Box>
		);
	}

	// Settings takeover: no app sidebar, top bar, or banner. The Settings page
	// brings its own header (Back button + search) and sidebar.
	if (isSettingsTakeover) {
		return (
			<Box position="relative" minH="100vh" bg="gray.50">
				<RecentActivityTracker />
				<NotificationToastManager onOpenEmsModal={handleOpenEmsModal} />
				<Box as="main" position="fixed" inset={0} overflow="hidden" bg="white" zIndex={20}>
					{children}
				</Box>
				{selectedEmsId && (
					<EMSDetailModal
						isOpen={isEmsModalOpen}
						onClose={handleCloseEmsModal}
						emsId={selectedEmsId}
					/>
				)}
				<KeyboardShortcutsModal
					isOpen={isKeyboardShortcutsOpen}
					onClose={() => setIsKeyboardShortcutsOpen(false)}
					modifierLabel={shortcutModifierLabel}
				/>
			</Box>
		);
	}

	return renderAppShell(
		children,
		<>
			<RecentActivityTracker />
			<NotificationToastManager onOpenEmsModal={handleOpenEmsModal} />
			{selectedEmsId && (
				<EMSDetailModal
					isOpen={isEmsModalOpen}
					onClose={handleCloseEmsModal}
					emsId={selectedEmsId}
				/>
			)}
			<KeyboardShortcutsModal
				isOpen={isKeyboardShortcutsOpen}
				onClose={() => setIsKeyboardShortcutsOpen(false)}
				modifierLabel={shortcutModifierLabel}
			/>
		</>,
	);
};

/** Ask Carl: admins only (same gate as the sidebar Home tab and command search). */
const AskCarlRoute = ({ children }: { children: React.ReactNode; }) => {
	const sessionContext = useContext(SessionContext);

	if (sessionContext?.loading) {
		return (
			<Box display="flex" justifyContent="center" alignItems="center" height="100vh">
				<Spinner size="xl" />
			</Box>
		);
	}
	if (!sessionContext?.isAuthenticated) {
		return <Navigate to="/login" replace />;
	}
	if (!canUseAskCarl(sessionContext)) {
		// Embed (mobile WebView): redirecting to '/' would bounce against the
		// embed route lock in ProtectedRoute and loop — show a notice instead.
		if (isEmbedMode(window.location.search)) {
			return (
				<Box display="flex" justifyContent="center" alignItems="center" height="100vh" px={8} textAlign="center" color="gray.600">
					Ask Carl isn&apos;t available for your account.
				</Box>
			);
		}
		return <Navigate to={'/'} replace />;
	}

	return <ProtectedRoute>{children}</ProtectedRoute>;
};

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			staleTime: 5 * 60 * 1000,
			gcTime: 10 * 60 * 1000,
			refetchOnWindowFocus: false,
		},
	},
});


const DefaultRedirect = () => {
	const sessionContext = useContext(SessionContext);
	if (!sessionContext?.isAuthenticated) {
		return <Navigate to="/login" replace />;
	}
	if (sessionContext.tabs.includes('ro-manager')) {
		return <Navigate to="/home" replace />;
	}
	if (sessionContext.tabs.includes('dashboard')) {
		return <Navigate to="/dashboard" replace />;
	}
	if (sessionContext.tabs.includes('actvity')) {
		return <Navigate to="/activity" replace />;
	}
	if (sessionContext.tabs.includes('inventory')) {
		return <Navigate to="/inventory" replace />;
	}
	if (sessionContext.tabs.includes('invoicing')) {
		return <Navigate to="/invoices" replace />;
	}
	if (sessionContext.tabs.includes('reporting')) {
		return <Navigate to="/reporting" replace />;
	}
	return < Navigate to="/login" replace />;
};
let hasInitializedPosthog = false;

const App = () => {
	// Latch embed mode (`?embed=1`) into sessionStorage on the very first
	// render — before any auth redirect can strip the query param — so the
	// whole session stays chrome-less (see utils/embedMode.ts).
	isEmbedMode(window.location.search);

	useEffect(() => {
		if (hasInitializedPosthog || !API_KEY) return;
		posthog.init(API_KEY, {
			api_host: API_HOST,
			capture_pageview: true,
			autocapture: true,
		});
		hasInitializedPosthog = true;
	}, []);

	return (
		<QueryClientProvider client={queryClient}>
			<SessionProvider>
				<ChakraProvider theme={theme} toastOptions={{ defaultOptions: DEFAULT_TOAST_OPTIONS }}>
					<BrowserRouter>
						<InvoiceUpdateJobWatcher />
						<ExtractionObservationJobWatcher />
						<BreadcrumbProvider>
							<BulkActionsShortcutProvider>
								<SidebarCollapseOverrideProvider>
									<ErrorBoundary>
												<Routes>
													<Route path="/login" element={<Login />} />
													<Route path="/create-account" element={<CreateAccount />} />
													<Route path="/create-account/invite/:inviteId" element={<InviteAccount />} />
													<Route path='/forgot-password' element={<ForgotPassword />} />
													<Route path="/status" element={<Status />} />
													<Route path="/:shopSlug/text-updates/:accessToken" element={<TextUpdatesOptIn />} />

													<Route path="/error/404" element={<Error404 />} />
													<Route path="/error/500" element={<Error500 />} />

													<Route
														path="/"
														element={<DefaultRedirect />}
													/>
													<Route
														path="/home"
														element={
															<ProtectedRoute>
																<Home />
															</ProtectedRoute>
														}
													/>
													<Route
														path="/activity"
														element={
															<ProtectedRoute>
																<ExportsPage />
															</ProtectedRoute>
														}
													/>
													<Route
														path="/profile"
														element={<Navigate to="/settings/profile" replace />}
													/>
													<Route
														path="/settings"
														element={
															<ProtectedRoute>
																<Settings />
															</ProtectedRoute>
														}
													/>
													<Route
														path="/settings/:sectionId"
														element={
															<ProtectedRoute>
																<Settings />
															</ProtectedRoute>
														}
													/>
													<Route
														path="/settings/users/:userId"
														element={
															<ProtectedRoute>
																<Settings />
															</ProtectedRoute>
														}
													/>
													{/* Detail pages and flows nested in a section
														(a vendor, say). Static child routes above
														this one still win, so users keeps :userId. */}
													<Route
														path="/settings/:sectionId/:detailId"
														element={
															<ProtectedRoute>
																<Settings />
															</ProtectedRoute>
														}
													/>
													<Route
														path="/settings/connections/:connectionId"
														element={
															<ProtectedRoute>
																<Settings />
															</ProtectedRoute>
														}
													/>
													<Route
														path="/subscription/success"
														element={
															<ProtectedRoute>
																<SubscriptionSuccess />
															</ProtectedRoute>
														}
													/>
													<Route
														path="/account-created"
														element={
															<ProtectedRoute>
																<AccountCreated />
															</ProtectedRoute>
														}
													/>
													<Route
														path="/dashboard"
														element={
															<ProtectedRoute>
																<OrdersPage />
															</ProtectedRoute>
														}
													/>
													<Route
														path="/delivery-board"
														element={
															<ProtectedRoute>
																<DeliveryBoard />
															</ProtectedRoute>
														}
													/>
													<Route
														path="/invoices"
														element={
															<ProtectedRoute>
																<InvoicesFeed />
															</ProtectedRoute>
														}
													/>
													<Route
														path="/invoices/:id"
														element={
															<ProtectedRoute>
																<InvoiceDetail />
															</ProtectedRoute>
														}
													/>
													<Route
														path="/inventory"
														element={
															<ProtectedRoute>
																<InventoryPage />
															</ProtectedRoute>
														}
													/>
													<Route
														path="/inventory/purchase-orders"
														element={
															<ProtectedRoute>
																<InventoryPage />
															</ProtectedRoute>
														}
													/>
													<Route
														path="/reporting"
														element={
															<ProtectedRoute>
																<ReportingPage />
															</ProtectedRoute>
														}
													/>
													<Route
														path="/order/:id"
														element={
															<ProtectedRoute>
																<OrderDetailPage />
															</ProtectedRoute>
														}
													/>
													<Route
														path="/ask-carl"
														element={
															<AskCarlRoute>
																<AskCarl />
															</AskCarlRoute>
														}
													/>
													<Route
														path="/chat"
														element={
															<ProtectedRoute>
																<Chats />
															</ProtectedRoute>
														}
													/>
													<Route
														path="/internal/supplier-reconcile"
														element={
															<ProtectedRoute>
																<SupplierReconcileReview />
															</ProtectedRoute>
														}
													/>
													<Route
														path="/internal/ccc-vendor-reconcile"
														element={
															<ProtectedRoute>
																<CccVendorReconcile />
															</ProtectedRoute>
														}
													/>
													<Route path="*" element={<Error404 />} />
												</Routes>
									</ErrorBoundary>
								</SidebarCollapseOverrideProvider>
							</BulkActionsShortcutProvider>
						</BreadcrumbProvider>
					</BrowserRouter>
				</ChakraProvider>
				<ReactQueryDevtools initialIsOpen={false} />
			</SessionProvider>
		</QueryClientProvider>
	);
};

export default App;
